import { c as _c } from "react-compiler-runtime";
import styled, { useTheme, css } from 'foundations-theming/utils';
import { memo, useCallback } from 'react';
import { keyframes } from 'styled-components';
import I18n from 'I18n';
import { EARTH_LAYER } from 'foundations-components/transitional/constants';
import { NotificationBadge } from 'foundations-components/transitional/components/NotificationBadge';
import { IconCircle } from 'foundations-components/transitional/components/IconCircle';
import useButtonA11yProps from 'collaboration-sidebar/hooks/useButtonA11yProps';
import useUnreadRepliesCount from '../hooks/useUnreadRepliesCount';
import { CommentsIcon } from 'foundations-assets/icons/components/CommentsIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BADGE_SIZE = 27;
const CommentIndicatorAnchor = styled.div.withConfig({
  displayName: "EmbeddedCommentIndicator__CommentIndicatorAnchor"
})(["position:relative;.sr-only{position:absolute !important;width:1px !important;height:1px !important;margin:-1px !important;padding:0 !important;overflow:hidden !important;clip:rect(0,0,0,0) !important;border:0 !important;}"]);
const fadeIn = keyframes(["0%{opacity:0;}100%{opacity:1;}"]);
const HoverableIconCircle = styled.span.withConfig({
  displayName: "EmbeddedCommentIndicator__HoverableIconCircle"
})(["position:relative;display:flex;border-radius:50%;z-index:", ";&:hover{cursor:pointer;", "}opacity:0;animation:0.1s ", " 0s 1 ease-out forwards;transition:box-shadow ease-out 150ms;pointer-events:all;"], EARTH_LAYER, ({
  boxShadow,
  theme
}) => boxShadow && `box-shadow: 0 0 0 1px ${theme.color['border-interactive-pressed']}, 0 0 8px 0 ${theme.color['border-interactive-pressed']};`, fadeIn);
const CommentIndicatorPointer = t0 => {
  "use memo";

  const $ = _c(2);
  const {
    fill: t1
  } = t0;
  const fill = t1 === undefined ? "fill-brand-default" : t1;
  const theme = useTheme();
  const t2 = theme.color[fill] || fill || "#FF7A59";
  let t3;
  if ($[0] !== t2) {
    t3 = /*#__PURE__*/_jsx("svg", {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 55.07 64.5",
      width: "100%",
      height: "100%",
      preserveAspectRatio: "none",
      children: /*#__PURE__*/_jsx("path", {
        d: "M18.652617 64.651013C40.374018 62.583472 56.004791 32.5 56.004791 32.5S40.374018 2.665303 18.652617.348987C-3.068785-1.967329 1.289013 32.5 1.289013 32.5s-4.357798 34.218554 17.363604 32.151013Z",
        fill: t2,
        fillRule: "evenodd"
      })
    });
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  return t3;
};
const SharedIconPointerStyled = css(["z-index:", ";position:absolute;inline-size:", "px;block-size:", "px;animation:0.1s ", " 0s 1 ease-out forwards;"], Number(EARTH_LAYER) - 1, BADGE_SIZE, BADGE_SIZE, fadeIn);
const HorizontalIconPointer = styled.span.attrs({
  ['data-test-id']: 'horizontal-pointer'
}).withConfig({
  displayName: "EmbeddedCommentIndicator__HorizontalIconPointer"
})(["", " inset-inline-start:6px;inset-block-start:0px;transform:scaleX(0.8);&:hover{cursor:pointer;}"], SharedIconPointerStyled);
const VerticalIconPointer = styled.span.attrs({
  ['data-test-id']: 'vertical-pointer'
}).withConfig({
  displayName: "EmbeddedCommentIndicator__VerticalIconPointer"
})(["", " transform:rotate(90deg) scaleX(0.8);inset-block-start:6px;inset-inline-start:0px;&:hover{cursor:pointer;}"], SharedIconPointerStyled);
const NotificationBadgePosition = styled.div.withConfig({
  displayName: "EmbeddedCommentIndicator__NotificationBadgePosition"
})(["position:absolute;inset-inline-end:-8px;inset-block-start:-10px;z-index:", ";&:hover{cursor:pointer;}"], EARTH_LAYER);
function EmbeddedCommentIndicator(t0) {
  "use memo";

  const $ = _c(44);
  const {
    collaborationSidebarState,
    isOpen,
    onClose,
    iconProps,
    renderCommentIndicator,
    setIsCommenting,
    verticalPositioning,
    hasCustomPosition,
    moduleParentComment,
    embeddedCommentWrapperRef,
    toggleOpenPopover
  } = t0;
  let t1;
  if ($[0] !== collaborationSidebarState || $[1] !== moduleParentComment) {
    t1 = {
      collaborationSidebarState,
      moduleParentComment
    };
    $[0] = collaborationSidebarState;
    $[1] = moduleParentComment;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    unreadCommentsCount
  } = useUnreadRepliesCount(t1);
  let t2;
  if ($[3] !== isOpen || $[4] !== onClose || $[5] !== setIsCommenting || $[6] !== toggleOpenPopover) {
    t2 = e => {
      if (isOpen && onClose) {
        onClose();
      } else {
        toggleOpenPopover(true);
        setIsCommenting(true);
        e.stopPropagation();
      }
    };
    $[3] = isOpen;
    $[4] = onClose;
    $[5] = setIsCommenting;
    $[6] = toggleOpenPopover;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const onIconClick = t2;
  const IconPointer = verticalPositioning ? VerticalIconPointer : HorizontalIconPointer;
  let t3;
  if ($[8] !== onIconClick) {
    t3 = {
      i18nKey: "collaborationSidebar.modals.commentMode.viewComments",
      onClick: onIconClick
    };
    $[8] = onIconClick;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const buttonA11yProps = useButtonA11yProps(t3);
  const isParentResolved = moduleParentComment === null || moduleParentComment === void 0 ? void 0 : moduleParentComment.resolved;
  const hoverableCircleBackgroundColor = isParentResolved ? "icon-primary-disabled" : "fill-brand-default";
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = I18n.text("collaborationSidebar.modals.commentMode.newComments");
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = unreadCommentsCount > 0;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = /*#__PURE__*/_jsx("span", {});
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== unreadCommentsCount) {
    t7 = /*#__PURE__*/_jsx(NotificationBadge, {
      badgeLabel: unreadCommentsCount,
      badgeDescription: t4,
      showBadge: t5,
      placement: "inline",
      children: t6
    });
    $[12] = t5;
    $[13] = unreadCommentsCount;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const notificationBadge = t7;
  if (renderCommentIndicator) {
    let t8;
    if ($[15] !== buttonA11yProps || $[16] !== notificationBadge || $[17] !== onIconClick || $[18] !== renderCommentIndicator) {
      t8 = renderCommentIndicator({
        buttonA11yProps,
        notificationBadge,
        onClick: onIconClick
      });
      $[15] = buttonA11yProps;
      $[16] = notificationBadge;
      $[17] = onIconClick;
      $[18] = renderCommentIndicator;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    const renderPropOutput = t8;
    if (renderPropOutput !== "use-default") {
      let t9;
      if ($[20] !== embeddedCommentWrapperRef || $[21] !== renderPropOutput) {
        t9 = /*#__PURE__*/_jsx(CommentIndicatorAnchor, {
          ref: embeddedCommentWrapperRef,
          children: renderPropOutput
        });
        $[20] = embeddedCommentWrapperRef;
        $[21] = renderPropOutput;
        $[22] = t9;
      } else {
        t9 = $[22];
      }
      return t9;
    }
  }
  const t8 = iconProps === null || iconProps === void 0 ? void 0 : iconProps.boxShadow;
  let t9;
  if ($[23] !== buttonA11yProps || $[24] !== hoverableCircleBackgroundColor || $[25] !== iconProps || $[26] !== onIconClick) {
    t9 = /*#__PURE__*/_jsx(IconCircle, Object.assign({
      icon: CommentsIcon,
      onClick: onIconClick,
      color: "text-core-on-fill-default",
      backgroundColor: hoverableCircleBackgroundColor,
      borderWidth: "0",
      blockSize: `${BADGE_SIZE}px`,
      inlineSize: `${BADGE_SIZE}px`,
      padding: "100"
    }, buttonA11yProps, iconProps));
    $[23] = buttonA11yProps;
    $[24] = hoverableCircleBackgroundColor;
    $[25] = iconProps;
    $[26] = onIconClick;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t8 || $[29] !== t9) {
    t10 = /*#__PURE__*/_jsx(HoverableIconCircle, {
      id: "comment-indicator",
      "data-test-id": "comment-indicator",
      className: "comment-indicator",
      boxShadow: t8,
      children: t9
    });
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== notificationBadge || $[32] !== onIconClick) {
    t11 = /*#__PURE__*/_jsx(NotificationBadgePosition, {
      id: "comment-indicator",
      onClick: onIconClick,
      children: notificationBadge
    });
    $[31] = notificationBadge;
    $[32] = onIconClick;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== IconPointer || $[35] !== hasCustomPosition || $[36] !== hoverableCircleBackgroundColor || $[37] !== onIconClick) {
    t12 = !hasCustomPosition && /*#__PURE__*/_jsx(IconPointer, {
      id: "comment-indicator",
      onClick: onIconClick,
      children: /*#__PURE__*/_jsx(CommentIndicatorPointer, {
        fill: hoverableCircleBackgroundColor
      })
    });
    $[34] = IconPointer;
    $[35] = hasCustomPosition;
    $[36] = hoverableCircleBackgroundColor;
    $[37] = onIconClick;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== embeddedCommentWrapperRef || $[40] !== t10 || $[41] !== t11 || $[42] !== t12) {
    t13 = /*#__PURE__*/_jsxs(CommentIndicatorAnchor, {
      ref: embeddedCommentWrapperRef,
      children: [t10, t11, t12]
    });
    $[39] = embeddedCommentWrapperRef;
    $[40] = t10;
    $[41] = t11;
    $[42] = t12;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  return t13;
}
export default /*#__PURE__*/memo(EmbeddedCommentIndicator);